import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Sparkles, Target, MessageCircle, TrendingUp, Eye, Send,
  BarChart3, Users, Bell, ChevronRight, Zap, CheckCircle2,
  ArrowRight, Clock, Heart, ThumbsUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══ MOCK DATA ═══ */
const DEMO_ADS = [
  { id: 1, day: 1, type: 'Problem', status: 'published', text: 'Masz dobra oferte, ale nikt nie pisze? To nie jest przypadek. Wiekszosc postow wyglada "ladnie", ale nie daje powodu do kontaktu.', image: `${API}/api/files/apisai/ares-pack/7fa5d954-a5f6-48a1-ba4b-25d42641969c/96dcebb0-6d77-4925-a7f7-86a0dffb11ad.png`, reactions: 47, comments: 12, messages: 8, score: 82 },
  { id: 2, day: 2, type: 'Insight', status: 'published', text: 'Problem nie jest w Twojej ofercie. Problem jest w tym, jak ja pokazujesz. Jedna zmiana w komunikacie potrafi podwoic zapytania.', image: `${API}/api/files/apisai/ares-pack/7fa5d954-a5f6-48a1-ba4b-25d42641969c/3fdf6fc5-097f-4394-8640-c8e33884e5b7.png`, reactions: 63, comments: 18, messages: 14, score: 91 },
  { id: 3, day: 3, type: 'Wartosc', status: 'published', text: '3 rzeczy, ktore zwiekszaja zapytania: jasny komunikat, konkret zamiast opisu, wezwanie do dzialania.', image: `${API}/api/files/apisai/ares-pack/7fa5d954-a5f6-48a1-ba4b-25d42641969c/1a629db9-94c5-4104-9995-a5a0c9503da3.png`, reactions: 55, comments: 9, messages: 11, score: 78 },
  { id: 4, day: 4, type: 'Proof', status: 'ready', text: 'Pierwsze wiadomosci moga pojawic sie szybciej niz myslisz. Czasem wystarczy jeden dobrze napisany post.', image: `${API}/api/files/apisai/ares-pack/7fa5d954-a5f6-48a1-ba4b-25d42641969c/e425321f-766c-4f5d-bf41-a997a2670e59.png`, reactions: 0, comments: 0, messages: 0, score: 0 },
  { id: 5, day: 5, type: 'CTA', status: 'ready', text: 'Chcesz, zeby ludzie zaczeli pisac wiadomosci? Pokaze Ci jak to zrobic krok po kroku. Napisz CHCE.', image: `${API}/api/files/apisai/ares-pack/7fa5d954-a5f6-48a1-ba4b-25d42641969c/eb3097a7-4245-4643-853a-28c59f738182.png`, reactions: 0, comments: 0, messages: 0, score: 0 },
];

const DEMO_LEADS = [
  { id: 1, name: 'Katarzyna M.', time: '12 min temu', message: 'Dzien dobry, interesuje mnie szczeniak z najblizszego miotu. Czy mozna uzyskac wiecej informacji?', status: 'new', avatar: 'K' },
  { id: 2, name: 'Piotr W.', time: '1h temu', message: 'Hej, widzialem Wasz post. Ile kosztuje szczeniak z rodowodem?', status: 'replied', avatar: 'P' },
  { id: 3, name: 'Anna S.', time: '3h temu', message: 'Czy macie wolne szczenieta? Szukam psa dla rodziny z dziecmi.', status: 'replied', avatar: 'A' },
  { id: 4, name: 'Tomasz K.', time: '5h temu', message: 'Witam, chcialbym zarezerwowac szczeniaka. Jak wyglada proces?', status: 'new', avatar: 'T' },
  { id: 5, name: 'Magdalena R.', time: 'wczoraj', message: 'Piekna hodowla! Czy moglabym odwiedzic i zobaczyc rodzicow?', status: 'replied', avatar: 'M' },
];

const DEMO_STATS = { views: 2847, messages: 47, reactions: 165, leads: 23 };

/* ═══ COMPONENTS ═══ */

function DemoSidebar({ activeSection, setActiveSection }) {
  const sections = [
    { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
    { id: 'ads', label: 'Reklamy', icon: Target },
    { id: 'leads', label: 'Zapytania', icon: MessageCircle },
    { id: 'stats', label: 'Wyniki', icon: TrendingUp },
  ];

  return (
    <div className="w-56 bg-[#0F0F10] border-r border-zinc-800 flex flex-col min-h-screen" data-testid="demo-sidebar">
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-[#F47A3B] flex items-center justify-center">
            <span className="text-white font-black text-sm">A</span>
          </div>
          <span className="text-white font-bold text-base tracking-tight">APISAI</span>
        </div>
        <div className="mt-3 px-2 py-1 bg-[#F47A3B]/10 border border-[#F47A3B]/20 rounded-lg text-center">
          <span className="text-[10px] font-bold text-[#F47A3B] uppercase tracking-wider">Tryb demo</span>
        </div>
      </div>

      <nav className="flex-1 p-3 space-y-1">
        {sections.map(s => {
          const Icon = s.icon;
          const active = activeSection === s.id;
          return (
            <button key={s.id} onClick={() => setActiveSection(s.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] transition-all ${active ? 'bg-[#18181B] text-[#F47A3B] font-semibold border-l-[3px] border-[#F47A3B] pl-[9px]' : 'text-zinc-400 hover:bg-[#18181B] hover:text-white border-l-[3px] border-transparent pl-[9px]'}`}
              data-testid={`demo-nav-${s.id}`}
            >
              <Icon className="w-4 h-4" /> {s.label}
              {s.id === 'leads' && <span className="ml-auto bg-red-500 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full">2</span>}
            </button>
          );
        })}
      </nav>

      <div className="p-3 border-t border-zinc-800">
        <div className="flex items-center gap-2 px-3 py-2">
          <div className="w-7 h-7 rounded-full bg-zinc-700 flex items-center justify-center text-[11px] font-bold text-white">D</div>
          <div>
            <p className="text-[11px] font-medium text-white">Demo Hodowla</p>
            <p className="text-[9px] text-zinc-500">demo@apisai.pl</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, change, color }) {
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-zinc-100 p-5 hover:shadow-lg transition-shadow"
      data-testid={`demo-stat-${label.toLowerCase()}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
        {change && <span className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">+{change}%</span>}
      </div>
      <p className="text-2xl font-black text-zinc-900">{value.toLocaleString('pl-PL')}</p>
      <p className="text-[12px] text-zinc-400 mt-0.5">{label}</p>
    </motion.div>
  );
}

function DemoDashboard() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold text-zinc-900 mb-1">Dashboard</h2>
        <p className="text-[13px] text-zinc-400">Podglad wynikow Twojej kampanii</p>
      </div>

      <div className="grid grid-cols-4 gap-4" data-testid="demo-stats-grid">
        <StatCard icon={Eye} label="Wyswietlenia" value={DEMO_STATS.views} change={34} color="bg-blue-50 text-blue-600" />
        <StatCard icon={MessageCircle} label="Wiadomosci" value={DEMO_STATS.messages} change={52} color="bg-[#F47A3B]/10 text-[#F47A3B]" />
        <StatCard icon={Heart} label="Reakcje" value={DEMO_STATS.reactions} change={28} color="bg-rose-50 text-rose-500" />
        <StatCard icon={Users} label="Leady" value={DEMO_STATS.leads} change={67} color="bg-emerald-50 text-emerald-600" />
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-2xl border border-zinc-100 p-5">
        <h3 className="text-[14px] font-bold text-zinc-900 mb-4">Ostatnia aktywnosc</h3>
        <div className="space-y-3">
          {[
            { icon: Send, text: 'Post "Masz dobra oferte..." opublikowany na FB', time: '2h temu', color: 'text-emerald-500 bg-emerald-50' },
            { icon: MessageCircle, text: 'Nowe zapytanie od Katarzyna M.', time: '12 min temu', color: 'text-[#F47A3B] bg-[#F47A3B]/10' },
            { icon: ThumbsUp, text: '47 reakcji na ostatni post', time: '3h temu', color: 'text-blue-500 bg-blue-50' },
            { icon: Bell, text: 'Auto-odpowiedz wyslana do Piotr W.', time: '1h temu', color: 'text-purple-500 bg-purple-50' },
          ].map((a, i) => (
            <div key={i} className="flex items-center gap-3 py-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.color}`}>
                <a.icon className="w-4 h-4" />
              </div>
              <p className="text-[12px] text-zinc-700 flex-1">{a.text}</p>
              <span className="text-[10px] text-zinc-400">{a.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DemoAds({ onPublish }) {
  const [publishedIds, setPublishedIds] = useState([]);
  const [publishingId, setPublishingId] = useState(null);

  const handleMockPublish = (adId) => {
    setPublishingId(adId);
    setTimeout(() => {
      setPublishedIds(prev => [...prev, adId]);
      setPublishingId(null);
      if (onPublish) onPublish();
      toast.success('Post opublikowany na Facebooku!', { description: 'To jest symulacja — w prawdziwym panelu post trafi na Twoja strone.' });
    }, 1500);
  };

  const getStatus = (ad) => {
    if (ad.status === 'published' || publishedIds.includes(ad.id)) return 'published';
    return 'ready';
  };

  const published = DEMO_ADS.filter(a => getStatus(a) === 'published');
  const ready = DEMO_ADS.filter(a => getStatus(a) === 'ready');

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-extrabold text-zinc-900 mb-1">Masz {DEMO_ADS.length} gotowych reklam</h2>
        <p className="text-[13px] text-zinc-400">{published.length} opublikowanych, {ready.length} gotowych do publikacji</p>
        <div className="flex items-center justify-center gap-3 mt-3">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-bold text-emerald-700">
            <CheckCircle2 className="w-3 h-3" /> {published.length} opublikowanych
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F47A3B]/10 border border-[#F47A3B]/20 text-[11px] font-bold text-[#F47A3B]">
            <Sparkles className="w-3 h-3" /> {ready.length} gotowych
          </span>
        </div>
      </div>

      {/* Published */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-emerald-500" />
          <h3 className="text-[12px] font-bold text-zinc-500 uppercase tracking-wider">Opublikowane ({published.length})</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {published.map(ad => (
            <motion.div key={ad.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: ad.id * 0.1 }}
              className="bg-white rounded-2xl border border-zinc-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              data-testid={`demo-ad-${ad.id}`}
            >
              <div className="w-full bg-zinc-900">
                <img src={ad.image} alt="" className="w-full h-auto" />
              </div>
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold text-zinc-400">D{ad.day}</span>
                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-bold">Opublikowany</span>
                </div>
                <p className="text-[11px] font-bold text-zinc-700 mb-2">{ad.type}</p>
                <p className="text-[10px] text-zinc-500 line-clamp-2">{ad.text}</p>
                <div className="flex items-center gap-3 mt-2 pt-2 border-t border-zinc-50">
                  <span className="text-[10px] text-zinc-400 flex items-center gap-1"><Heart className="w-3 h-3" /> {ad.reactions}</span>
                  <span className="text-[10px] text-zinc-400 flex items-center gap-1"><MessageCircle className="w-3 h-3" /> {ad.comments}</span>
                  <span className="text-[10px] text-[#F47A3B] font-bold flex items-center gap-1 ml-auto"><Zap className="w-3 h-3" /> {ad.score}pkt</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Ready */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-[#F47A3B]" />
          <h3 className="text-[12px] font-bold text-zinc-500 uppercase tracking-wider">Gotowe reklamy ({ready.length})</h3>
        </div>
        <div className="grid grid-cols-3 gap-4">
          {ready.map(ad => (
            <motion.div key={ad.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: ad.id * 0.1 }}
              className="bg-white rounded-2xl border border-zinc-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
              data-testid={`demo-ad-${ad.id}`}
            >
              <div className="w-full bg-zinc-900">
                <img src={ad.image} alt="" className="w-full h-auto" />
              </div>
              <div className="p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold text-zinc-400">D{ad.day}</span>
                  <span className="text-[9px] px-2 py-0.5 rounded-full bg-[#F47A3B]/10 text-[#F47A3B] border border-[#F47A3B]/20 font-bold">Gotowa</span>
                </div>
                <p className="text-[11px] font-bold text-zinc-700 mb-2">{ad.type}</p>
                <p className="text-[10px] text-zinc-500 line-clamp-2">{ad.text}</p>
                <button
                  onClick={() => handleMockPublish(ad.id)}
                  disabled={publishingId === ad.id}
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-[#F47A3B] text-white text-[11px] font-bold hover:bg-[#E06A2B] transition-colors disabled:opacity-50"
                  data-testid={`demo-publish-${ad.id}`}
                >
                  {publishingId === ad.id ? (
                    <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Publikowanie...</>
                  ) : (
                    <><Send className="w-3 h-3" /> Publikuj</>
                  )}
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DemoLeads() {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold text-zinc-900 mb-1">Zapytania od klientow</h2>
        <p className="text-[13px] text-zinc-400">{DEMO_LEADS.length} zapytan, {DEMO_LEADS.filter(l => l.status === 'new').length} nowych</p>
      </div>

      <div className="space-y-3" data-testid="demo-leads-list">
        {DEMO_LEADS.map((lead, i) => (
          <motion.div key={lead.id} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }}
            className="bg-white rounded-2xl border border-zinc-100 p-4 hover:shadow-lg transition-shadow cursor-pointer flex items-start gap-4"
            data-testid={`demo-lead-${lead.id}`}
          >
            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-[14px] font-bold ${lead.status === 'new' ? 'bg-[#F47A3B] text-white' : 'bg-zinc-100 text-zinc-600'}`}>
              {lead.avatar}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-[13px] font-bold text-zinc-900">{lead.name}</p>
                {lead.status === 'new' && <span className="text-[9px] px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 font-bold">Nowe</span>}
                {lead.status === 'replied' && <span className="text-[9px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200 font-bold">Odpowiedziano</span>}
                <span className="text-[10px] text-zinc-400 ml-auto">{lead.time}</span>
              </div>
              <p className="text-[12px] text-zinc-500 truncate">{lead.message}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-300 mt-3" />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function DemoStats() {
  const weekData = [
    { day: 'Pon', views: 420, messages: 8 },
    { day: 'Wt', views: 380, messages: 5 },
    { day: 'Sr', views: 510, messages: 12 },
    { day: 'Czw', views: 450, messages: 7 },
    { day: 'Pt', views: 620, messages: 14 },
    { day: 'Sob', views: 280, messages: 3 },
    { day: 'Nd', views: 187, messages: 2 },
  ];
  const maxViews = Math.max(...weekData.map(d => d.views));

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-extrabold text-zinc-900 mb-1">Wyniki kampanii</h2>
        <p className="text-[13px] text-zinc-400">Ostatnie 7 dni</p>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid="demo-chart">
          <h3 className="text-[13px] font-bold text-zinc-700 mb-4">Wyswietlenia</h3>
          <div className="flex items-end gap-2 h-40">
            {weekData.map((d, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                <div className="w-full rounded-t-md bg-[#F47A3B]/20 relative" style={{ height: `${(d.views / maxViews) * 100}%` }}>
                  <div className="absolute bottom-0 left-0 right-0 rounded-t-md bg-[#F47A3B]" style={{ height: `${(d.messages / 14) * 100}%`, minHeight: '4px' }} />
                </div>
                <span className="text-[9px] text-zinc-400">{d.day}</span>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-zinc-50">
            <span className="flex items-center gap-1.5 text-[10px] text-zinc-400"><span className="w-2 h-2 rounded-sm bg-[#F47A3B]/20" /> Wyswietlenia</span>
            <span className="flex items-center gap-1.5 text-[10px] text-zinc-400"><span className="w-2 h-2 rounded-sm bg-[#F47A3B]" /> Wiadomosci</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-zinc-100 p-5" data-testid="demo-performance">
          <h3 className="text-[13px] font-bold text-zinc-700 mb-4">Skutecznosc postow</h3>
          <div className="space-y-3">
            {[
              { label: 'Problem (przyciaga)', score: 82, color: 'bg-red-400' },
              { label: 'Insight (edukuje)', score: 91, color: 'bg-blue-400' },
              { label: 'Wartosc (buduje)', score: 78, color: 'bg-emerald-400' },
              { label: 'Proof (zaufanie)', score: 85, color: 'bg-purple-400' },
              { label: 'CTA (zamyka)', score: 88, color: 'bg-[#F47A3B]' },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[11px] text-zinc-600">{item.label}</span>
                  <span className="text-[11px] font-bold text-zinc-800">{item.score}pkt</span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${item.score}%` }} transition={{ delay: i * 0.15, duration: 0.6 }}
                    className={`h-full rounded-full ${item.color}`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-gradient-to-r from-[#F47A3B]/5 to-transparent rounded-2xl border border-[#F47A3B]/10 p-5" data-testid="demo-conversion">
        <h3 className="text-[13px] font-bold text-zinc-700 mb-2">Konwersja</h3>
        <div className="grid grid-cols-4 gap-4 text-center">
          {[
            { label: 'Wyswietlenia', value: '2 847' },
            { label: 'Klikniecia', value: '384' },
            { label: 'Wiadomosci', value: '47' },
            { label: 'Konwersja', value: '1.65%' },
          ].map((s, i) => (
            <div key={i}>
              <p className="text-xl font-black text-zinc-900">{s.value}</p>
              <p className="text-[10px] text-zinc-400 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


/* ═══ CONTEXTUAL HINT SIDEBAR ═══ */
function ContextHint({ publishCount, activeSection }) {
  // Determine state
  let state = 'welcome';
  if (activeSection === 'leads') state = 'leads';
  else if (activeSection === 'stats') state = 'stats';
  else if (publishCount >= 2) state = 'momentum';
  else if (publishCount === 1) state = 'first_publish';
  else if (activeSection === 'ads') state = 'ready';

  const hints = {
    welcome: {
      icon: Target,
      iconBg: 'bg-[#F47A3B]/10 text-[#F47A3B]',
      title: 'Witaj w ApisAI',
      text: 'Masz gotowe reklamy. Przejdź do zakładki "Reklamy" i kliknij Publikuj — my zajmiemy się resztą.',
      tip: 'Kliknij "Reklamy" w menu po lewej',
      progress: 0,
    },
    ready: {
      icon: Sparkles,
      iconBg: 'bg-[#F47A3B]/10 text-[#F47A3B]',
      title: 'To sa gotowe reklamy',
      text: 'Kazda ma grafike, tekst i jest gotowa do publikacji. Kliknij "Publikuj" zeby zaczac.',
      tip: 'Wybierz dowolna reklame i kliknij Publikuj',
      progress: 20,
    },
    first_publish: {
      icon: MessageCircle,
      iconBg: 'bg-emerald-50 text-emerald-600',
      title: 'Tak zaczynaja pojawiac sie zapytania',
      text: 'Po publikacji automatycznie odpowiadamy na komentarze i zbieramy leady. Pierwsze wiadomości mogą przyjść w ciągu godzin.',
      tip: 'Publikuj kolejne — kazdy post zwieksza zasieg',
      progress: 50,
    },
    momentum: {
      icon: TrendingUp,
      iconBg: 'bg-blue-50 text-blue-600',
      title: 'Zwiekszasz zasieg i klientow',
      text: 'Im wiecej publikujesz, tym wiecej osob Cie widzi. System uczy sie, ktore posty dzialaja najlepiej.',
      tip: 'Sprawdz zakladke "Wyniki" zeby zobaczyc efekty',
      progress: 80,
    },
    leads: {
      icon: Users,
      iconBg: 'bg-purple-50 text-purple-600',
      title: 'Zapytania od klientow',
      text: 'System sam klasyfikuje wiadomosci i wysyla auto-odpowiedzi. Ty tylko finalizujesz kontakt.',
      tip: 'Kliknij w zapytanie zeby zobaczyc szczegoly',
      progress: 70,
    },
    stats: {
      icon: BarChart3,
      iconBg: 'bg-blue-50 text-blue-600',
      title: 'Twoje wyniki rosna',
      text: 'System analizuje co dziala i dostosowuje kolejne posty. Lepsze treści = wiecej zapytan.',
      tip: 'Porownaj typy postow — zobacz co przyciaga klientow',
      progress: 90,
    },
  };

  const h = hints[state] || hints.welcome;
  const Icon = h.icon;

  return (
    <div className="w-64 border-l border-zinc-100 bg-white flex flex-col sticky top-0 h-screen" data-testid="demo-context-hint">
      <AnimatePresence mode="wait">
        <motion.div
          key={state}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
          className="p-5 flex flex-col gap-4"
        >
          {/* Icon */}
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${h.iconBg}`}>
            <Icon className="w-6 h-6" />
          </div>

          {/* Title */}
          <div>
            <h3 className="text-[14px] font-bold text-zinc-900 mb-1">{h.title}</h3>
            <p className="text-[12px] text-zinc-500 leading-relaxed">{h.text}</p>
          </div>

          {/* Tip */}
          <div className="bg-[#F47A3B]/5 border border-[#F47A3B]/10 rounded-xl p-3">
            <div className="flex items-start gap-2">
              <Zap className="w-3.5 h-3.5 text-[#F47A3B] mt-0.5 shrink-0" />
              <p className="text-[11px] text-[#F47A3B] font-medium">{h.tip}</p>
            </div>
          </div>

          {/* Progress */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] text-zinc-400 font-medium">Postep demo</span>
              <span className="text-[10px] font-bold text-zinc-600">{h.progress}%</span>
            </div>
            <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${h.progress}%` }}
                transition={{ duration: 0.6 }}
                className="h-full rounded-full bg-[#F47A3B]"
              />
            </div>
          </div>

          {/* Published count */}
          {publishCount > 0 && (
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 text-center">
              <p className="text-2xl font-black text-emerald-600">{publishCount}</p>
              <p className="text-[10px] text-emerald-500 font-medium">{publishCount === 1 ? 'post opublikowany' : 'posty opublikowane'}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Bottom CTA in sidebar */}
      <div className="mt-auto p-4 border-t border-zinc-100">
        <a href="/rejestracja"
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-[#F47A3B] text-white text-[11px] font-bold hover:bg-[#E06A2B] transition-all"
          data-testid="demo-hint-cta"
        >
          <Sparkles className="w-3.5 h-3.5" /> Zrob swoja reklame
        </a>
      </div>
    </div>
  );
}


/* ═══ MAIN PAGE ═══ */
export default function DemoPanel() {
  const [activeSection, setActiveSection] = useState('dashboard');
  const [publishCount, setPublishCount] = useState(0);
  const navigate = useNavigate();

  const handleDemoPublish = () => {
    setPublishCount(prev => prev + 1);
  };

  const content = {
    dashboard: <DemoDashboard />,
    ads: <DemoAds onPublish={handleDemoPublish} />,
    leads: <DemoLeads />,
    stats: <DemoStats />,
  };

  return (
    <div className="flex min-h-screen bg-[#FAFAFA]" data-testid="demo-panel">
      <DemoSidebar activeSection={activeSection} setActiveSection={setActiveSection} />

      <div className="flex-1 flex flex-col">
        {/* Top bar */}
        <div className="h-14 border-b border-zinc-100 bg-white flex items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-bold text-[#F47A3B] bg-[#F47A3B]/10 px-3 py-1 rounded-full border border-[#F47A3B]/20">
              DEMO — Tryb pokazowy
            </span>
          </div>
          <button onClick={() => navigate('/rejestracja')}
            className="flex items-center gap-2 px-5 py-2 rounded-xl bg-[#F47A3B] text-white text-[12px] font-bold hover:bg-[#E06A2B] transition-all shadow-lg shadow-[#F47A3B]/20"
            data-testid="demo-cta-top"
          >
            <Sparkles className="w-4 h-4" /> Zrob swoja reklame <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Content + Context Hint */}
        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 p-6 max-w-5xl mx-auto w-full overflow-y-auto">
            {content[activeSection]}
          </div>
          <ContextHint publishCount={publishCount} activeSection={activeSection} />
        </div>

        {/* Bottom CTA */}
        <div className="border-t border-zinc-100 bg-white p-4" data-testid="demo-cta-bottom">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[14px] font-bold text-zinc-800 mb-2">Chcesz takie wyniki dla swojej hodowli?</p>
            <p className="text-[12px] text-zinc-400 mb-3">System sam tworzy reklamy, publikuje je i zbiera zapytania. Ty tylko odpowiadasz klientom.</p>
            <button onClick={() => navigate('/rejestracja')}
              className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-[#F47A3B] text-white text-[13px] font-bold hover:bg-[#E06A2B] transition-all shadow-lg shadow-[#F47A3B]/20"
              data-testid="demo-cta-main"
            >
              <Zap className="w-4 h-4" /> Zacznij za darmo <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
